import moment from 'moment'
import { RootState } from '.'
import type { AnyCreditsAction } from '../actions/credits'
import type { Credit, CreditType } from '../types'
import { ArrayUtils } from '../utils'

const initialState = {
    credits: [] as Credit[],
    types: [] as CreditType[],
    error: undefined as string | undefined,
    isWorking: false,
    selectedCredit: {
        data: undefined as Credit | undefined,
        error: undefined as string | undefined,
        isWorking: false,
    },
}

export type CreditsState = typeof initialState

type RawCredit = Credit

function formatCreditData(rawCredit: RawCredit): Credit {
    return {
        ...rawCredit,
        start_date: moment(rawCredit.start_date, 'YYYY-MM-DD').toDate(),
        end_date: moment(rawCredit.end_date, 'YYYY-MM-DD').toDate(),
    }
}

function creditsReducer(state: CreditsState = initialState, { type, payload }: AnyCreditsAction) {
    switch (type) {
        case 'CREDITS_FETCH##REQUEST':
            return {
                ...state,
                isWorking: true,
                error: undefined,
                credits: initialState.credits,
            }
        case 'CREDITS_FETCH##SUCCESS':
            return {
                ...state,
                credits: payload.map(formatCreditData),
                isWorking: false,
                error: undefined,
            }
        case 'CREDITS_FETCH##FAILURE':
            return {
                ...state,
                isWorking: false,
                error: payload.message,
            }
        case 'CREDIT_ID_FETCH##REQUEST':
            return {
                ...state,
                selectedCredit: {
                    data: undefined,
                    isWorking: true,
                    error: undefined,
                },
            }
        case 'CREDIT_ID_FETCH##SUCCESS':
            return {
                ...state,
                selectedCredit: {
                    data: formatCreditData(payload),
                    isWorking: false,
                    error: undefined,
                },
            }
        case 'CREDIT_ID_FETCH##FAILURE':
            return {
                ...state,
                selectedCredit: {
                    data: undefined,
                    isWorking: false,
                    error: payload.message,
                },
            }
        case 'CREDIT_TYPES_FETCH##REQUEST':
            return {
                ...state,
                types: initialState.types,
            }
        case 'CREDIT_TYPES_FETCH##SUCCESS':
            return {
                ...state,
                types: payload,
            }
        case 'CREDIT_DELETE##SUCCESS': {
            const index = state.credits.findIndex(credit => credit.id === payload)
            const updatedCredits = ArrayUtils.removeItem(state.credits, index)
            let { selectedCredit } = state

            if (selectedCredit.data?.id === payload) {
                selectedCredit = initialState.selectedCredit
            }

            return {
                ...state,
                credits: updatedCredits,
                selectedCredit,
            }
        }
        case 'CREDIT_UPDATE##SUCCESS': {
            const index = state.credits.findIndex(credit => credit.id === payload.id)
            const updatedCredits = ArrayUtils.insertItem(
                state.credits,
                index,
                formatCreditData(payload),
            )

            return {
                ...state,
                credits: updatedCredits,
            }
        }
        case 'CREDIT_CREATE##SUCCESS': {
            return {
                ...state,
                credits: [
                    ...state.credits,
                    formatCreditData(payload),
                ],
            }
        }
        default:
        return state
    }
}

function isWorking(state: RootState): boolean {
    return state.credits.isWorking
}

function selectCredits(state: RootState): Credit[] {
    return state.credits.credits
}

function isLoadingSelectedCredit(state: RootState): boolean {
    return state.credits.selectedCredit.isWorking
}

function selectedCredit(state: RootState): Credit | undefined {
    return state.credits.selectedCredit.data
}

function selectCreditTypes(state: RootState): CreditType[] {
    return state.credits.types
}

export default creditsReducer

export {
    isLoadingSelectedCredit,
    isWorking,
    selectCredits,
    selectedCredit,
    selectCreditTypes,
}
